import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular/standalone';
import { environment } from '../../../environments/environment';
import {
  AppError,
  ErrorCategory,
  ErrorSeverity,
  USER_ERROR_MESSAGES
} from '../models/error.models';
import { ErrorLoggerService } from './error-logger.service';
import { devError } from '@core';

/**
 * Global error handler for CheckerFly application
 * Captures, categorizes, logs, and displays user-friendly error messages
 *
 * @example
 * // Automatically captures all unhandled errors
 * throw new Error('Something went wrong'); // User sees friendly toast
 *
 * // HTTP errors are categorized automatically
 * this.http.get('/api/endpoint'); // 404 shows "Resource not found"
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private router!: Router;
  private toastController!: ToastController;
  private errorLogger!: ErrorLoggerService;

  constructor(private injector: Injector) {
    // Lazy injection to avoid circular dependencies
    // Services not available in constructor, must get in handleError()
  }

  /**
   * Main error handling entry point
   * Called by Angular for all unhandled errors
   */
  handleError(error: unknown): void {
    // Lazy inject services (avoids circular dependency issues)
    if (!this.router) {
      this.router = this.injector.get(Router);
      this.toastController = this.injector.get(ToastController);
      this.errorLogger = this.injector.get(ErrorLoggerService);
    }

    // Categorize and structure the error
    const appError = this.categorizeError(error);

    // Log error (async, non-blocking)
    this.errorLogger.logError(appError).catch(err => {
      // Use devError for CLAUDE.md compliance
      // In production, this is a no-op (fails silently as intended)
      // In development, logs to console for debugging
      devError('Error logger failed:', err);
    });

    // Show user-friendly notification
    this.showErrorToast(appError);

    // In development, also log to console for debugging
    if (!environment.production) {
      devError('[GlobalErrorHandler]', error);
    }
  }

  /**
   * Categorize error type and extract relevant information
   */
  private categorizeError(error: Error | HttpErrorResponse | unknown): AppError {
    let category: ErrorCategory;
    let severity: ErrorSeverity;
    let message: string;

    // Handle non-object errors (strings like "Timeout")
    if (typeof error !== 'object' || error === null) {
      return {
        message: USER_ERROR_MESSAGES[ErrorCategory.CLIENT],
        originalError: error instanceof Error ? error : new Error(String(error)),
        category: ErrorCategory.CLIENT,
        severity: ErrorSeverity.MEDIUM,
        context: {
          userId: this.getCurrentUserId(),
          route: this.router?.url || 'unknown',
          timestamp: Date.now(),
          appVersion: this.getAppVersion(),
          userAgent: navigator.userAgent,
          additionalData: {
            errorName: 'UnknownError',
            errorMessage: String(error)
          }
        },
        stackTrace: undefined
      };
    }

    if (error instanceof HttpErrorResponse) {
      // HTTP errors (network, API)
      category = ErrorCategory.HTTP;
      severity = error.status >= 500 ? ErrorSeverity.HIGH : ErrorSeverity.MEDIUM;
      message = this.getHttpErrorMessage(error);
    } else if (this.isFirebaseError(error)) {
      // Firebase SDK errors
      category = ErrorCategory.FIREBASE;
      severity = ErrorSeverity.MEDIUM;
      message = this.getFirebaseErrorMessage(error);
    } else if ((error as Error).name === 'UnhandledPromiseRejectionWarning') {
      // Async errors
      category = ErrorCategory.ASYNC;
      severity = ErrorSeverity.MEDIUM;
      message = USER_ERROR_MESSAGES[ErrorCategory.ASYNC];
    } else {
      // Generic client-side errors
      category = ErrorCategory.CLIENT;
      severity = ErrorSeverity.MEDIUM;
      message = USER_ERROR_MESSAGES[ErrorCategory.CLIENT];
    }

    // Safe property access for error objects
    const errorObj = error as Error;
    return {
      message,
      originalError: errorObj,
      category,
      severity,
      context: {
        userId: this.getCurrentUserId(),
        route: this.router?.url || 'unknown',
        timestamp: Date.now(),
        appVersion: this.getAppVersion(),
        userAgent: navigator.userAgent,
        additionalData: {
          errorName: errorObj.name || 'UnknownError',
          errorMessage: errorObj.message || String(error)
        }
      },
      stackTrace: errorObj.stack
    };
  }

  /**
   * Get user-friendly message for HTTP errors
   */
  private getHttpErrorMessage(error: HttpErrorResponse): string {
    const statusMessages = USER_ERROR_MESSAGES[ErrorCategory.HTTP];

    if (error.status in statusMessages) {
      return statusMessages[error.status as keyof typeof statusMessages] as string;
    }

    return statusMessages.default;
  }

  /**
   * Check if error is from Firebase SDK
   *
   * **WI-028**: Type-safe check using unknown + type narrowing
   *
   * @param error - Error to check
   * @returns true if error has Firebase error code format
   */
  private isFirebaseError(error: unknown): boolean {
    return (
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      typeof (error as { code: unknown }).code === 'string' && (
        (error as { code: string }).code.startsWith('auth/') ||
        (error as { code: string }).code.startsWith('firestore/') ||
        (error as { code: string }).code === 'permission-denied'
      )
    );
  }

  /**
   * Get user-friendly message for Firebase errors
   *
   * **WI-028**: Type-safe Firebase error message lookup
   *
   * @param error - Firebase error object
   * @returns User-friendly error message
   */
  private getFirebaseErrorMessage(error: unknown): string {
    const firebaseMessages = USER_ERROR_MESSAGES[ErrorCategory.FIREBASE];

    // Type guard for error with code property
    if (
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      typeof (error as { code: unknown }).code === 'string'
    ) {
      const code = (error as { code: string }).code;
      if (code in firebaseMessages) {
        return firebaseMessages[code as keyof typeof firebaseMessages] as string;
      }
    }

    return firebaseMessages.default;
  }

  /**
   * Get current authenticated user ID (if available)
   *
   * Note: AuthService is available (WI-012 complete), but userId is intentionally
   * NOT included in error logs to minimize PII exposure. Only include userId if
   * explicitly required for debugging user-specific issues, and ensure GDPR/CCPA
   * compliance (data retention policies, user consent).
   *
   * @returns undefined (always 'anonymous' in logs for privacy)
   */
  private getCurrentUserId(): string | undefined {
    // Intentionally return undefined to minimize PII in error logs
    // If userId is needed for debugging, integrate AuthService here:
    // const auth = this.injector.get(AuthService);
    // return auth.currentUser?.uid;
    return undefined;
  }

  /**
   * Get application version from environment
   */
  private getAppVersion(): string {
    // In production, this should come from package.json or environment
    // For now, return simple version string
    return environment.production ? '1.0.0' : 'dev';
  }

  /**
   * Display user-friendly error toast notification
   *
   * **Suppression Rules** (HF-001 fix):
   * - Only show toasts for errors that block user actions
   * - LOW severity: Never show toast (background errors, logging failures)
   * - MEDIUM severity: Only show if error prevents user action
   * - HIGH/CRITICAL: Always show toast
   *
   * **Rationale**: Avoid showing toasts for recoverable Firebase/reCAPTCHA errors
   * that don't actually block the user flow (e.g., rate limit check failures that
   * are caught and handled, reCAPTCHA initialization retries).
   */
  private async showErrorToast(appError: AppError): Promise<void> {
    // Suppress toasts for LOW severity errors (background errors)
    if (appError.severity === ErrorSeverity.LOW) {
      return;
    }

    // Suppress toasts for MEDIUM severity Firebase errors (often recoverable)
    // These are typically transient issues that don't block user flow
    if (appError.severity === ErrorSeverity.MEDIUM &&
        appError.category === ErrorCategory.FIREBASE) {
      return;
    }

    try {
      const toast = await this.toastController.create({
        message: appError.message,
        duration: 5000,
        position: 'top',
        color: appError.severity === ErrorSeverity.CRITICAL ? 'danger' : 'warning',
        buttons: [
          {
            text: 'Dismiss',
            role: 'cancel'
          }
        ],
        cssClass: 'error-toast'
      });

      await toast.present();
    } catch (toastError) {
      // Fallback: If toast fails, log via devError
      // In production, this is a no-op (fails silently)
      // In development, helps debug toast issues
      devError('Failed to show error toast:', toastError);
      devError('Original error:', appError.message);
    }
  }
}
